import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { api } from '@/api/client'
import { csrfToken } from '@/api/csrf'
import { useMe } from '@/api/me'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Rails redirects failed magic links, invites and Google sign-ins here with ?error=… (#6, #7, #8).
const searchSchema = z.object({
  error: z.enum(['magic_link_invalid', 'invitation_invalid', 'google_not_invited', 'google_failed']).optional(),
})

const linkErrorKeys = {
  magic_link_invalid: 'login.magicLinkInvalid',
  invitation_invalid: 'login.invitationInvalid',
  google_not_invited: 'login.googleNotInvited',
  google_failed: 'login.googleFailed',
} as const

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: searchSchema,
})

function LoginPage() {
  const { t } = useTranslation()
  const { error: linkError } = Route.useSearch()
  const me = useMe()
  const requestLink = api.useMutation('post', '/api/magic_link')

  const formSchema = z.object({ email: z.email({ message: t('login.invalidEmail') }) })
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = form.handleSubmit(({ email }) => {
    requestLink.mutate({ body: { email } })
  })

  // Already signed in: no reason to ask for a link.
  if (me.isSuccess && me.data) return <Navigate to="/" replace />

  const rateLimited = requestLink.isError && requestLink.error?.error?.key === 'rate_limited'
  const fieldError = form.formState.errors.email?.message ?? ''
  const requestError = requestLink.isError ? t(rateLimited ? 'login.rateLimited' : 'login.failed') : ''

  return (
    <main className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl">{t('login.title')}</CardTitle>
          <CardDescription>{t('login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/* Live regions stay mounted, so screen readers announce the text when it appears. */}
          <p role="status" aria-live="polite" className="text-sm empty:hidden">
            {requestLink.isSuccess ? t('login.sent') : ''}
          </p>
          <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
            {linkError ? t(linkErrorKeys[linkError]) : requestError}
          </p>

          {/* A document POST, not a fetch: OmniAuth redirects to Google and the callback signs in
              with a redirect (#8). omniauth-rails_csrf_protection checks the token. */}
          <form method="post" action="/auth/google_oauth2">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <Button type="submit" variant="outline" className="h-11 w-full">
              {t('login.googleSubmit')}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">{t('login.or')}</p>

          {/* The form stays after sending, so a typo can be corrected without a reload. */}
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t('login.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                className="h-11"
                placeholder={t('login.emailPlaceholder')}
                aria-invalid={Boolean(fieldError)}
                aria-describedby="email-error"
                {...form.register('email')}
              />
              <p id="email-error" role="alert" className="text-sm text-destructive empty:hidden">
                {fieldError}
              </p>
            </div>

            <Button type="submit" className="h-11" disabled={requestLink.isPending}>
              {t(requestLink.isPending ? 'login.submitting' : 'login.submit')}
            </Button>
          </form>

          {/* The public Workspace request form (09 Onboarding, #227). */}
          <p className="text-center text-sm text-muted-foreground">
            {t('login.noAccess')}{' '}
            <Link to="/sign_up" className="font-medium text-foreground underline underline-offset-4">
              {t('login.requestAccess')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  )
}
